/**
 * NeedRfqsView — 한 need 의 모든 RFQ batch + supplier 응답
 *
 * URL:    /need/:id/rfqs
 * 진입:   DealDetailView 의 소싱 섹션에서 "보기" 클릭 → 새 탭
 *
 * 책임 분리:
 *   - 이 페이지     "이 부품을 어디에 얼마에 물어봤지?" (한 need history)
 *   - /rfq 페이지   "전체 견적 현황" (전역, 카테고리/공급사 그룹) — 별도
 *
 * Sola loader 패턴 (ADR-0002) 적용:
 *   - main.ts 의 라우트 정의에서 fetchNeedRfqs 를 loader 로
 *   - view 생성 시점에 data 가 완전 (need + batches non-null)
 *   - 분기 X, 우회 X
 *
 * 레이아웃:
 *   - 헤더:  딜로 돌아가기 링크 + need 정보 + 상태
 *   - 본문:  batch 별 supplier 응답 표 (시간순)
 */

import { SolaView, htmlSolaWithInstance, navigate } from '@hm-spt/sola';
import * as api from '../api/client';
import { escapeHtml, fmtDate, fmtDateTime } from '../lib/cells';
import { renderConditionChip, renderConditionChips, renderRegistrationChip } from '../lib/condition-chip';

/** main.ts 의 loader 결과 = NeedRfqsResponse 그대로. */
export type NeedRfqsLoaded = api.NeedRfqsResponse;

interface NeedRfqsData extends NeedRfqsLoaded {
  needId: string;
}

export class NeedRfqsView extends SolaView<NeedRfqsData> {
  override events = {
    click: {
      '@back-to-deal':   'handleBackToDeal',
    },
  };

  constructor(params: { needId: string } & NeedRfqsLoaded) {
    super();
    this.data = params;
  }

  override getTitle(): string {
    const n = this.data.need;
    const label = n.option_part || n.item_text || n.need_no || `NEED-${n.id}`;
    return `${label} — RFQ 응답 — HCLTech`;
  }

  render(): HTMLElement {
    return htmlSolaWithInstance(this)`
      <article class="need-rfqs-page">
        ${this.renderHeader()}
        ${this.renderBatches()}
      </article>
    `;
  }

  // ─── 헤더 — 딜 링크 + need 요약 ────────────────────────────────────
  private renderHeader(): string {
    const n = this.data.need;
    const title = n.option_part
      ? n.option_part
      : (n.item_text ?? n.need_no ?? `NEED-${n.id}`);
    const subtitle = n.option_part && n.item_text
      ? n.item_text
      : '';

    return `
      <header class="need-rfqs-header">
        <div class="need-rfqs-nav">
          <a class="need-rfqs-back"
             data-action="back-to-deal"
             href="/deal/${escapeHtml(n.deal_id)}">
            ← 딜로 돌아가기
          </a>
        </div>

        <h1 class="need-rfqs-title">
          ${escapeHtml(title)}
          ${renderRegistrationChip(n.option_part)}
        </h1>
        ${subtitle ? `<p class="need-rfqs-subtitle">${escapeHtml(subtitle)}</p>` : ''}

        <dl class="need-rfqs-meta">
          <div><dt>NEED No.</dt><dd><code>${escapeHtml(n.need_no ?? `#${n.id}`)}</code></dd></div>
          <div><dt>카테고리</dt><dd>${escapeHtml(n.category ?? '-')}</dd></div>
          <div><dt>요청 수량</dt><dd>${n.quantity}</dd></div>
          <div><dt>요청 컨디션</dt><dd>${renderConditionChips(n.requested_condition)}</dd></div>
          <div><dt>상태</dt><dd>${escapeHtml(n.status)}</dd></div>
          <div><dt>응답 요약</dt><dd>${escapeHtml(n.rfq_summary ?? '-')}</dd></div>
          <div><dt>요청일</dt><dd>${escapeHtml(fmtDate(n.created_at))}</dd></div>
        </dl>
      </header>
    `;
  }

  // ─── 본문 — batch 별 표 ────────────────────────────────────────────
  private renderBatches(): string {
    const batches = this.data.batches;
    if (batches.length === 0) {
      return `
        <section class="need-rfqs-empty">
          <p>아직 RFQ 를 보내지 않았습니다.</p>
          <p class="text-muted">딜 상세 페이지의 품목 섹션에서 [RFQ 요청하기] 로 보낼 수 있습니다.</p>
        </section>
      `;
    }

    return batches.map((b, idx) => this.renderBatch(b, idx === 0)).join('');
  }

  private renderBatch(b: api.NeedRfqBatch, isFirst: boolean): string {
    const label = isFirst ? '첫 요청' : '재요청';

    const rows = b.rfqs.map((r) => `
      <tr>
        <td class="text-muted text-sm"><code>${escapeHtml(r.rfq_no ?? `#${r.id}`)}</code></td>
        <td>${escapeHtml(r.supplier_name)}</td>
        <td>${escapeHtml(r.status)}</td>
        <td class="text-right">${fmtPrice(r.price_usd)}</td>
        <td>${renderConditionChip(r.sourced_condition)}</td>
        <td>${escapeHtml(r.alternative_part ?? '-')}</td>
        <td>${escapeHtml(r.ajj_memo ?? '-')}</td>
        <td>${r.received_at ? escapeHtml(fmtDateTime(r.received_at)) : '<span class="text-muted">-</span>'}</td>
      </tr>
    `).join('');

    return `
      <section class="need-rfqs-batch">
        <header class="need-rfqs-batch-header">
          <h2>Batch ${escapeHtml(b.group_code)}</h2>
          <span class="need-rfqs-batch-meta">
            ${escapeHtml(fmtDateTime(b.requested_at))} · ${label} · ${b.rfqs.length}개 공급사
          </span>
        </header>

        <div class="need-rfqs-table-wrap">
          <table class="need-rfqs-table">
            <thead>
              <tr>
                <th>RFQ No.</th>
                <th>공급사</th>
                <th>상태</th>
                <th class="text-right">단가 (USD)</th>
                <th>컨디션</th>
                <th>대체 부품</th>
                <th>메모</th>
                <th>응답일</th>
              </tr>
            </thead>
            <tbody>${rows}</tbody>
          </table>
        </div>
      </section>
    `;
  }

  // ─── 이벤트 ────────────────────────────────────────────────────────
  handleBackToDeal(e: Event, target: HTMLElement): void {
    e.preventDefault();
    const href = target.getAttribute('href');
    if (href) navigate(href);
  }
}

// ─── 헬퍼 — 가격 포맷 ────────────────────────────────────────────────
function fmtPrice(raw: string | null): string {
  if (raw == null || raw === '') return '<span class="text-muted">-</span>';
  // NUMERIC 문자열을 숫자로 변환 + 천 단위 구분
  const n = Number(raw);
  if (!isFinite(n)) return escapeHtml(raw);
  return n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}
